// src/components/ModelSettingsPage.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../services/firebase';
import { motion } from 'framer-motion';
import PhotoGallery from '../components/PhotoGallery';

const SERVICE_LABELS = {
  podolatria: 'Podolatria',
  sexo_anal: 'Sexo anal',
  sexo_oral: 'Sexo oral',
  massagem: 'Massagem relaxante',
  companhia: 'Companhia para eventos',
  roleplay: 'Roleplay/Fantasia',
  bdsm: 'BDSM',
};

const ToggleButton = ({ isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#e91e63] focus:ring-offset-2 ${
      isActive ? 'bg-[#e91e63]' : 'bg-gray-200'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        isActive ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

export default function ModelSettings() {
  const { currentUser, loading: authLoading } = useAuth();
  const { username: routeUsername } = useParams();
  const navigate = useNavigate();

  // Determine username: either from URL param or from logged-in user's username
  const username = routeUsername || currentUser?.username;

  const [videoUrl, setVideoUrl] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [services, setServices] = useState({});
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [activeDay, setActiveDay] = useState(1); // 0=Dom…6=Sáb
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pictures, setPictures] = useState(Array(12).fill(''));
  const [uploading, setUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [activeWeek, setActiveWeek] = useState(0);

  // Generate 7×24 empty slots
  function generateEmptySlots() {
    const arr = [];
    for (let dow = 0; dow < 7; dow++) {
      for (let h = 0; h < 24; h++) {
        arr.push({ dayOfWeek: dow, hour: h, isAvailable: false });
      }
    }
    return arr;
  }

  // Redirect if not authenticated and no route param
  useEffect(() => {
    if (authLoading) return; // Don't redirect while auth is loading
    
    if (!currentUser && !routeUsername) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, routeUsername, currentUser, navigate]);

  // Load GP settings once we know username
  useEffect(() => {
    if (!username) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const gpDoc = doc(db, 'models', username);
        const snap = await getDoc(gpDoc);
        
        if (!snap.exists()) {
          console.error('GP not found:', username);
          setLoading(false);
          return;
        }

        const data = snap.data();
        setVideoUrl(data.videoUrl || '');
        setAudioUrl(data.audioUrl || '');
        setServices(data.services || {});
        setAvailabilitySlots(data.availabilitySlots || generateEmptySlots());
        
        // Initialize pictures array with 12 slots, filling with existing pictures
        const existingPictures = data.pictures || [];
        // Filter out any empty or invalid URLs
        const validPictures = existingPictures.filter(url => url && url.trim() !== '');
        const picturesArray = Array(12).fill('').map((_, index) => validPictures[index] || '');
        setPictures(picturesArray);
        setPhotoUrl(data.photoUrl || '');

        // If we have valid pictures but no cover photo, set the first valid picture as cover
        if (validPictures.length > 0 && !data.photoUrl) {
          setPhotoUrl(validPictures[0]);
          await updateDoc(doc(db, 'models', username), { photoUrl: validPictures[0] });
        }

        // Log the pictures for debugging
        console.log('Loaded pictures:', picturesArray);
      } catch (error) {
        console.error('Error loading GP data:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [username]);

  // Handle picture upload
  async function handlePictureUpload(index, file) {
    if (!file || !username) return;
    
    setUploading(true);
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Por favor, selecione apenas imagens.');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('A imagem deve ter no máximo 5MB.');
      }

      // Create a reference to the file location in Firebase Storage
      const storageRef = ref(storage, `models/${username}/pictures/${index}`);
      
      // Upload the file
      await uploadBytes(storageRef, file);
      
      // Get the download URL with public access
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update the pictures array
      const newPictures = [...pictures];
      newPictures[index] = downloadURL;
      setPictures(newPictures);

      // If this is the first picture or there's no cover photo, set it as cover
      if (!photoUrl) {
        await handleSetAsCover(index);
      }

      // Update Firestore document with the new picture URL
      await updateDoc(doc(db, 'models', username), {
        pictures: newPictures.filter(url => url !== '')
      });

    } catch (error) {
      console.error('Error uploading picture:', error);
      alert(error.message || 'Erro ao fazer upload da imagem. Tente novamente.');
    } finally {
      setUploading(false);
    }
  }

  // Handle picture deletion
  async function handlePictureDelete(index) {
    if (!username) return;
    
    try {
      const currentPhoto = pictures[index];
      if (currentPhoto) {
        // Try to delete from Firebase Storage, but don't fail if it doesn't exist
        try {
          const storageRef = ref(storage, `models/${username}/pictures/${index}`);
          await deleteObject(storageRef);
        } catch (error) {
          // If the error is because the object doesn't exist, we can continue
          if (error.code !== 'storage/object-not-found') {
            throw error;
          }
          console.log('Image already deleted from storage');
        }
        
        // Update the pictures array
        const newPictures = [...pictures];
        newPictures[index] = '';
        setPictures(newPictures);

        // If this was the cover photo, clear the cover photo
        if (currentPhoto === photoUrl) {
          setPhotoUrl('');
          await updateDoc(doc(db, 'models', username), { photoUrl: '' });
        }

        // Update Firestore document to remove the picture URL
        const updatedPictures = newPictures.filter(url => url !== '');
        await updateDoc(doc(db, 'models', username), {
          pictures: updatedPictures
        });
      }
    } catch (error) {
      console.error('Error deleting picture:', error);
      alert('Erro ao deletar a imagem. Tente novamente.');
    }
  }

  // Handle setting a picture as cover
  async function handleSetAsCover(index) {
    if (!username || !pictures[index]) return;
    
    try {
      const newPhotoUrl = pictures[index];
      setPhotoUrl(newPhotoUrl);
      await updateDoc(doc(db, 'models', username), { 
        photoUrl: newPhotoUrl,
        pictures: pictures.filter(url => url !== '')
      });
    } catch (error) {
      console.error('Error setting cover photo:', error);
      alert('Erro ao definir foto de capa. Tente novamente.');
    }
  }

  // Toggle a service on/off
  function toggleService(key) {
    setServices(prev => ({ ...prev, [key]: !prev[key] }));
  }

  // Toggle a slot for the active day
  function toggleSlot(hour) {
    setAvailabilitySlots(prev => {
      const newSlots = prev.map(s =>
        s.dayOfWeek === activeDay && s.hour === hour
          ? { ...s, isAvailable: !s.isAvailable }
          : s
      );
      return newSlots;
    });
  }

  // Save all changes to Firestore
  async function handleSave() {
    if (!username) return;
    
    setSaving(true);
    try {
      // Filter out empty slots from pictures array before saving
      const picturesToSave = pictures.filter(url => url !== '');
      await updateDoc(doc(db, 'models', username), {
        videoUrl,
        audioUrl,
        services,
        availabilitySlots,
        pictures: picturesToSave,
        photoUrl
      });
      alert('Configurações salvas!');
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  // Get next 14 days with their day of week
  const getNextDays = () => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push({
        date,
        dayOfWeek: date.getDay(),
        label: date.toLocaleDateString('pt-BR', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        })
      });
    }
    return days;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#e91e63] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  if (!username) return null;

  return (
    <div className="min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 leading-relaxed">Configurações</h1>
          <p className="text-gray-600 text-lg leading-relaxed">Gerencie suas informações e disponibilidade</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Photos and Media */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Fotos</h2>
              <p className="text-gray-600 mb-4">Adicione até 12 fotos para seu perfil</p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {pictures.map((photo, index) => (
                  <div key={index} className="aspect-square relative group">
                    {photo ? (
                      <>
                        <img
                          src={photo}
                          alt={`Foto ${index + 1}`}
                          className="w-full h-full object-cover rounded-2xl"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handlePictureDelete(index)}
                              className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                              title="Excluir foto"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleSetAsCover(index)}
                              className={`p-2 rounded-full transition-colors ${
                                photo === photoUrl
                                  ? 'bg-[#e91e63] text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-100'
                              }`}
                              title={photo === photoUrl ? "Foto de capa" : "Definir como capa"}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <label className="w-full h-full flex items-center justify-center border-2 border-dashed border-gray-300 rounded-2xl cursor-pointer hover:border-[#e91e63] transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePictureUpload(index, e.target.files[0])}
                          disabled={uploading}
                        />
                        <div className="text-center">
                          <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span className="text-sm text-gray-500">Adicionar foto</span>
                        </div>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Mídia</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL do Vídeo
                  </label>
                  <input
                    type="text"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    className="w-full h-12 px-4 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#e91e63] focus:border-transparent transition-all duration-200"
                    placeholder="Cole a URL do seu vídeo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL do Áudio
                  </label>
                  <input
                    type="text"
                    value={audioUrl}
                    onChange={(e) => setAudioUrl(e.target.value)}
                    className="w-full h-12 px-4 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#e91e63] focus:border-transparent transition-all duration-200"
                    placeholder="Cole a URL do seu áudio"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Services and Availability */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Serviços</h2>
              <p className="text-gray-600 mb-6">Selecione quais serviços você oferece</p>
              
              <div className="space-y-4">
                {Object.entries(SERVICE_LABELS).map(([key, label]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-200 hover:border-[#e91e63]/50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        services[key] ? 'bg-[#e91e63]/10' : 'bg-gray-100'
                      }`}>
                        <svg 
                          className={`w-5 h-5 ${services[key] ? 'text-[#e91e63]' : 'text-gray-400'}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="2" 
                            d="M5 13l4 4L19 7" 
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{label}</h3>
                        <p className="text-sm text-gray-500">
                          {services[key] ? 'Serviço oferecido' : 'Serviço não oferecido'}
                        </p>
                      </div>
                    </div>
                    <ToggleButton
                      isActive={services[key]}
                      onClick={() => toggleService(key)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Disponibilidade</h2>
              <p className="text-gray-600 mb-6">Configure seus horários disponíveis para os próximos 14 dias</p>
              
              {/* Week Selector */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Selecione a Semana</h3>
                <div className="flex space-x-3">
                  {(() => {
                    const today = new Date();
                    const nextWeek = new Date(today);
                    nextWeek.setDate(today.getDate() + 7);
                    
                    return [
                      {
                        label: `Esta Semana (${today.getDate()}/${today.getMonth() + 1} - ${new Date(today.getTime() + 6 * 24 * 60 * 60 * 1000).getDate()}/${new Date(today.getTime() + 6 * 24 * 60 * 60 * 1000).getMonth() + 1})`,
                        value: 0
                      },
                      {
                        label: `Próxima Semana (${nextWeek.getDate()}/${nextWeek.getMonth() + 1} - ${new Date(nextWeek.getTime() + 6 * 24 * 60 * 60 * 1000).getDate()}/${new Date(nextWeek.getTime() + 6 * 24 * 60 * 60 * 1000).getMonth() + 1})`,
                        value: 1
                      }
                    ];
                  })().map(({ label, value }) => (
                    <button
                      key={value}
                      onClick={() => setActiveWeek(value)}
                      className={`flex-1 px-4 py-3 rounded-2xl text-sm transition-all duration-200 ${
                        activeWeek === value
                          ? 'bg-[#e91e63] text-white'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Day Selector */}
              <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
                {(() => {
                  const today = new Date();
                  const startDate = new Date(today);
                  startDate.setDate(today.getDate() + (activeWeek * 7));
                  
                  return [
                    { label: 'Domingo', value: 0 },
                    { label: 'Segunda', value: 1 },
                    { label: 'Terça', value: 2 },
                    { label: 'Quarta', value: 3 },
                    { label: 'Quinta', value: 4 },
                    { label: 'Sexta', value: 5 },
                    { label: 'Sábado', value: 6 }
                  ].map(({ label, value }) => {
                    const date = new Date(startDate);
                    date.setDate(startDate.getDate() + (value - startDate.getDay() + 7) % 7);
                    return {
                      label,
                      value,
                      date
                    };
                  });
                })().map(({ label, value, date }) => (
                  <button
                    key={label}
                    onClick={() => setActiveDay(value)}
                    className={`px-4 py-2 rounded-2xl transition-all duration-200 whitespace-nowrap ${
                      activeDay === value
                        ? 'bg-[#e91e63] text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <span>{label}</span>
                      <span className="text-xs mt-1">{date.getDate()}/{date.getMonth() + 1}</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700">Horários Disponíveis</h3>
                  <button
                    onClick={() => {
                      const currentDaySlots = availabilitySlots.filter(s => s.dayOfWeek === activeDay);
                      const allAvailable = currentDaySlots.every(s => s.isAvailable);
                      
                      setAvailabilitySlots(prev => 
                        prev.map(s => 
                          s.dayOfWeek === activeDay 
                            ? { ...s, isAvailable: !allAvailable }
                            : s
                        )
                      );
                    }}
                    className="text-sm text-[#e91e63] hover:text-[#d81557] transition-colors"
                  >
                    {availabilitySlots.filter(s => s.dayOfWeek === activeDay).every(s => s.isAvailable)
                      ? 'Desmarcar todos'
                      : 'Marcar todos'}
                  </button>
                </div>

                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-2">
                  {Array.from({ length: 24 }, (_, i) => i).map((hour) => {
                    const isAvailable = availabilitySlots.find(s => s.dayOfWeek === activeDay && s.hour === hour)?.isAvailable;
                    return (
                      <button
                        key={hour}
                        onClick={() => toggleSlot(hour)}
                        className={`p-3 rounded-2xl text-sm transition-all duration-200 ${
                          isAvailable
                            ? 'bg-[#e91e63] text-white hover:bg-[#d81557]'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {hour.toString().padStart(2, '0')}h
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 lg:mt-10 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="h-12 px-8 bg-[#e91e63] text-white rounded-2xl font-medium shadow-md hover:bg-[#d81557] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#e91e63] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
            ) : (
              'Salvar Alterações'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
